import React, { useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { animated, useTransition } from "@react-spring/web";
import _ from "lodash";
import cx from "classnames";

import { formatDurationWithLabel } from "../utils/format";
import styles from "./common.module.scss";
import TruncateWithTooltip from "src/components/truncateWithTooltip";

export type PageAnalyticsValueKey = "views" | "totalTime";

export interface PageAnalyticsChartProps {
  data?: any[];
  valueKey?: PageAnalyticsValueKey;
}

interface PageSegment {
  index: number;
  value: number;
  heightPercent: number;
  percentage: number;
  displayValue: string;
  label: string;
  thumbnail?: string;
  isPeak: boolean;
}

const getRawValue = (item: any, valueKey: PageAnalyticsValueKey): number => {
  if (valueKey === "totalTime") {
    return Number(_.get(item, "totalTime", 0)) || 0;
  }
  return Number(_.get(item, "views", _.get(item, "count", 0))) || 0;
};

const getReadPercentage = (item: any, value: number, maxValue: number): number => {
  const explicit =
    _.get(item, "percentageCompleted") ??
    _.get(item, "percentage") ??
    _.get(item, "completion") ??
    _.get(item, "readProgress");

  if (explicit !== undefined && explicit !== null && !Number.isNaN(Number(explicit))) {
    return Math.min(100, Math.max(0, Number(explicit)));
  }
  if (!maxValue) return 0;
  return Math.min(100, Math.round((value / maxValue) * 100));
};

const formatDisplayValue = (value: number, valueKey: PageAnalyticsValueKey): string => {
  if (valueKey === "totalTime") {
    return formatDurationWithLabel(value).value;
  }
  return String(Math.round(value));
};

const normalizeSegments = (data: any[], valueKey: PageAnalyticsValueKey): PageSegment[] => {
  if (!data?.length) return [];

  const sorted = [...data].sort(
    (a, b) => (Number(a.index) || 0) - (Number(b.index) || 0),
  );

  const values = sorted.map((item) => getRawValue(item, valueKey));
  const maxValue = Math.max(...values, 1);
  const peakValue = Math.max(...values);

  return sorted.map((item, i) => {
    const index = Number(item.index) || i + 1;
    const value = getRawValue(item, valueKey);
    const percentage = getReadPercentage(item, value, maxValue);
    const name = _.get(item, "name");

    return {
      index,
      value,
      heightPercent: maxValue ? Math.round((value / maxValue) * 100) : 0,
      percentage,
      displayValue: formatDisplayValue(value, valueKey),
      label: name ? String(name) : `Page ${index}`,
      thumbnail: _.get(item, "thumbnail"),
      isPeak: value > 0 && value === peakValue,
    };
  });
};

export const PageAnalyticsChart: React.FC<PageAnalyticsChartProps> = ({
  data = [],
  valueKey = "views",
}) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [anchor, setAnchor] = useState<{ x: number; y: number; bottom: number } | null>(null);
  const [tooltipPos, setTooltipPos] = useState<{ left: number; top: number } | null>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const barWrapRefs = useRef<(HTMLDivElement | null)[]>([]);

  const segments = useMemo(() => normalizeSegments(data, valueKey), [data, valueKey]);

  const activeSegment = activeIndex !== null ? segments[activeIndex] : null;

  const tooltipTransition = useTransition(activeSegment, {
    keys: activeSegment ? activeSegment.index : null,
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
    config: { tension: 400, friction: 30 },
  });

  useLayoutEffect(() => {
    const tooltip = tooltipRef.current;
    if (!tooltip || !anchor) {
      setTooltipPos(null);
      return;
    }
    const margin = 8;
    const { offsetWidth: w, offsetHeight: h } = tooltip;
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    let left = anchor.x - w / 2;
    left = Math.max(margin, Math.min(left, vw - w - margin));

    let top = anchor.y - h - margin;
    if (top < margin) top = anchor.bottom + margin;
    top = Math.min(top, vh - h - margin);

    setTooltipPos({ left, top });
  }, [anchor, activeIndex]);

  if (!segments.length) return null;

  const handleBarEnter = (index: number) => {
    setTooltipPos(null);
    setActiveIndex(index);
    const barEl = barWrapRefs.current[index];
    if (barEl) {
      const rect = barEl.getBoundingClientRect();
      setAnchor({ x: rect.left + rect.width / 2, y: rect.top, bottom: rect.bottom });
    }
  };

  const handleBarLeave = () => {
    setActiveIndex(null);
    setAnchor(null);
  };

  return (
    <div className={styles.pageAnalyticsChart}>
      {createPortal(
        tooltipTransition((style, segment) =>
          segment ? (
            <animated.div
              ref={tooltipRef}
              className={styles.pageAnalyticsHeader}
              style={{
                ...style,
                position: "fixed",
                left: tooltipPos?.left ?? 0,
                top: tooltipPos?.top ?? 0,
                visibility: tooltipPos ? "visible" : "hidden",
              }}
            >
                <TruncateWithTooltip text={segment.label} className={styles.pageAnalyticsHeaderLabel} />
              {segment.thumbnail && (
                <img
                  className={styles.pageAnalyticsHeaderThumb}
                  src={segment.thumbnail}
                  alt=""
                  height={80}
                  width={80}
                />
              )}
              <TruncateWithTooltip text={segment.displayValue} className={styles.pageAnalyticsHeaderValue} />
            </animated.div>
          ) : null,
        ),
        document.body,
      )}

      <div className={styles.pageAnalyticsBars}>
        {segments.map((segment, i) => {
          const isActive = activeIndex === i;
          const fillHeight = Math.max(segment.heightPercent, 8);

          return (
            <div
              key={`${segment.index}-${i}`}
              ref={(el) => {
                barWrapRefs.current[i] = el;
              }}
              className={styles.pageAnalyticsBarWrap}
              onMouseEnter={() => handleBarEnter(i)}
              onMouseLeave={handleBarLeave}
            >
              <div className={styles.pageAnalyticsTrack}>
                <div
                  className={cx(styles.pageAnalyticsFill, {
                    [styles.pageAnalyticsFillActive]: isActive,
                  })}
                  style={{ height: `${fillHeight}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
